import * as React from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { currentConnectedERPselector } from '@/domain/settings';
import { rtlEnable } from '@/domain/env';
import { isJeLayoutRTLSelector } from '@/domain/journal';
import { getMetaRange } from '@/components/Tables/helpers';
import { isMandatory, isActive, getDataCell } from '@/components/Tables/layout/helpers';

import JETable from '@/components/Tables/grid/JETable';
import { JETableOverride } from '@/components/Tables/layout/JEHeadersTable/StyledComponents';
import { JE_TABLE_CLASSES } from '@/components/Tables/grid/StyledComponents';

import cx from 'classnames';

/**
 * Renders the JE header (settings) table.
 *
 * The `data` prop is already resolved via `rearrangedSettingsSelector` in
 * Tables/index.jsx. Cells that are null (hidden or vacated by a field rearrangement) render
 * as empty <td> elements — same as cells that simply have no data from the API.
 */
class JEHeadersTable extends React.Component {
  get meta() {
    const { rawData, data, rtl, isJeLayoutRTL } = this.props;
    // Use rawData (un-rearranged) for layout — this preserves all original
    // columns even when entire columns are hidden/vacated by field rearrangement.
    // Use data (rearranged) for cell content only (via getData/getItem).
    // Fall back to data if rawData is absent or empty.
    const metaSource = rawData && rawData.size > 0 ? rawData : data;
    const [cols, rows] = getMetaRange(metaSource, rtl || isJeLayoutRTL);

    // DA-18804: Trim trailing empty row-pairs in display mode.
    // Header fields come in pairs: odd row = description, even row = value.
    // Only trailing pairs where ALL cells are absent from rearranged data are
    // removed — empty pairs between non-empty ones are preserved so the
    // visual layout stays consistent.  Rows are kept as-is when rawData is
    // not available (no rearrangement active).
    if (rawData && rawData.size > 0 && data) {
      // Helper: check if a row-pair has at least one visible cell.
      const isPairNonEmpty = (r) => {
        const headerRow = r % 2 === 1 ? r : r - 1;
        const valueRow = headerRow + 1;
        return cols.some((c) => data.has(`${c}${headerRow}`) || data.has(`${c}${valueRow}`));
      };

      // Find the last non-empty row-pair and keep everything up to it.
      // Iterate from the end of the rows array to find the last row that
      // belongs to a non-empty pair.
      let lastNonEmptyIdx = rows.length - 1;
      while (lastNonEmptyIdx >= 0 && !isPairNonEmpty(rows[lastNonEmptyIdx])) {
        lastNonEmptyIdx--;
      }
      const filteredRows = rows.slice(0, lastNonEmptyIdx + 1);
      return [cols, filteredRows];
    }

    return [cols, rows];
  }

  getCellStyle = (col, row) => {
    const { active } = this.props;
    const entry = this.getData(row, col);
    return cx({
      [JE_TABLE_CLASSES.CELL_MANDATORY]: isMandatory(entry),
      [JE_TABLE_CLASSES.CELL_ACTIVE]: isActive(col, row, active),
    });
  };

  getData = (row, col) => {
    // getDataCell returns undefined for missing cells; applyFieldRearrangement
    // returns null for hidden/vacated ones. Both render as empty <td>.
    return getDataCell(this.props.data, row, col) || undefined;
  };

  getItem = (row, col) => this.props.getItem(this.getData(row, col));

  render() {
    return (
      <JETableOverride>
        <JETable meta={this.meta} getCellStyle={this.getCellStyle} getItem={this.getItem} prevItem={() => null} />
      </JETableOverride>
    );
  }
}

const mapStateToProps = (state) => ({
  erp: currentConnectedERPselector(state),
  rtl: rtlEnable(state),
  isJeLayoutRTL: isJeLayoutRTLSelector(state),
});

export default compose(connect(mapStateToProps))(JEHeadersTable);
